import {
  ImageUp,
  SquarePen,
  Trash2,
  CopyPlus,
  Loader2,
  X,
  Download,
  Thermometer,
  Scale,
  Activity,
  Heart,
  Droplet,
  CandyCane,
  Wind,
  Salad,
  Gauge,
  Stethoscope,
  Upload,
  Calendar,
  Save,
  CheckCircle,
  FileText,
  PlusCircle,
  Baby,
  CalendarClock,
  HeartPulse,
} from "lucide-react";
import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { PatientService } from "../../services/api/patient.service";
import toast from "react-hot-toast";
import { AppointmentService } from "../../services/api/appointment.service";
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";
import { firestore as db } from "../../firebase/firebase";
import { MedicalCampService } from "../../services/api/medicalCamp.Service";
import FindDoctorBox from "../Appointments/FindDoctorBox";
import FilePreview from "../preview/FilePreview.tsx";
import CancelAppointmentModal from "./CancelAppointmentModal.tsx";
import { PatientVitalsType, PrescriptionImagesType } from "./type.ts";
import { AuthService } from "../../services/api/auth.service.ts";

interface VITALS {
  patientId: string;
  appointmentId: string;
  weight: string;
  height: string;
  temperature: string;
  pulseRate: string;
  heartRate: string;
  spo2: string;
  glucose: string;
  respiratoryRate: string;
  vegetarian: string;
  bloodPressureMin: string;
  bloodPressureMax: string;
  patientConcerns: string;
  ongoingMedication: string[];
  image: File[];
}

interface PatientVitialsPropsType {
  disableAccess?: boolean;
  appointment: any;
  vitalsFormData: PatientVitalsType;
  setVitalsFormData: React.Dispatch<React.SetStateAction<PatientVitalsType>>;
  currentPrescriptions: File[];
  setCurrentPrescriptions: React.Dispatch<React.SetStateAction<File[]>>;
  onGoingPrescriptions: PrescriptionImagesType[];
  setOnGoingPrescriptions: React.Dispatch<
    React.SetStateAction<PrescriptionImagesType[]>
  >;
  patient: any;
  loadAppointments: () => void;
  setIsmarkasready: (id: boolean) => void;
  onClose: () => void;
  destination: string;
  hasVitals: boolean;
  setHasVitals: React.Dispatch<React.SetStateAction<boolean>>;
  buttonName: string;
  setButtonName: React.Dispatch<React.SetStateAction<string>>;
  disable: boolean;
  setDisable: React.Dispatch<React.SetStateAction<boolean>>;
  isPatientAvailable: boolean;
  currentDate: string | undefined;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isTreatedPatient?: boolean;
  isPregnant?: boolean;
  setIsPregnant: React.Dispatch<React.SetStateAction<boolean>>;
}
export function PatientVitials({
  disableAccess,
  appointment,
  patient,
  loadAppointments,
  onClose,
  setIsmarkasready,
  destination,
  vitalsFormData,
  setVitalsFormData,
  onGoingPrescriptions,
  setOnGoingPrescriptions,
  currentPrescriptions,
  setCurrentPrescriptions,
  hasVitals,
  setHasVitals,
  buttonName,
  setButtonName,
  disable,
  setDisable,
  isPatientAvailable,
  currentDate,
  isLoading,
  setIsLoading,
  isTreatedPatient,
  isPregnant,
  setIsPregnant,
}: PatientVitialsPropsType) {
  const { user } = useAuth();
  const [selectedDoctor, setSelectedDoctor] = useState<string | null>(null);
  const [isSearchingDoctors, setIsSearchingDoctors] = useState(false);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [isAppointmentBooked, setisAppointmentBooked] = useState(false);
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [isRescheduling, setIsrescheduling] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isVitalsAdded, setIsVitalsAdded] = useState(false);
  const dropZoneRef = useRef<HTMLLabelElement>(null);
  const [selectedFileDetatils, setSelectedFileDetails] = useState({
    status: false,
    type: "",
    url: "",
  });
  const [patientAppointmentHistory, setPatientAppointmentHistory] = useState(
    {}
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Add this function to sanitize filenames
  const sanitizeFileName = (fileName: string) => {
    // Get file extension
    const extension = fileName.split('.').pop();

    // Get base name without extension
    const baseName = fileName.substring(0, fileName.lastIndexOf('.'));

    // Remove special characters and spaces from base name
    const sanitizedBaseName = baseName
      .replace(/[^a-zA-Z0-9]/g, '') // Remove all non-alphanumeric characters
      .toLowerCase();

    // Add timestamp to ensure uniqueness
    const timestamp = new Date().getTime();

    // Combine parts to create final sanitized filename
    return `${sanitizedBaseName}${timestamp}.${extension}`;
  };

  useEffect(() => {
    if (buttonName === "Edit Vitals") {
      setIsVitalsAdded(true);
    } else if (buttonName === "Add Vitals") {
      setIsVitalsAdded(false);
    }
  }, [buttonName]);

  const formattedDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure 2 digits
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  if (patient?.userId) {
    useEffect(() => {
      if (!patient?.userId) return; // Prevents calling API if userId is missing

      const fetchAppointmentHistory = async () => {
        try {
          setIsLoading(true);
          const response = await PatientService.getPatientHistoryForAppointment(
            patient.userId
          );
          setPatientAppointmentHistory(
            response.data.patientAppointmentHistory[0]
          );
        } catch (error) {
          console.log(error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchAppointmentHistory();
    }, [patient.userId]);
  }

  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}`;
  };

  const handleFindDoctors = async (selectedDate?: string) => {
    if (typeof selectedDate !== "string") {
      selectedDate = formattedDate(); // Default to today's date
    }

    setIsSearchingDoctors(true);
    try {
      console.log("API call with date:", selectedDate); // Debugging log

      if (
        user?.role.toLocaleLowerCase() === "clinic" ||
        user?.role.toLocaleLowerCase() === "admin"
      ) {
        await findClinicDoctors(selectedDate);
      } else if (user?.role.toLocaleLowerCase() === "medicalcamp") {
        await findCampDoctors(selectedDate);
      }
    } catch (error) {
      console.error("Error finding doctors:", error);
      toast.error("Failed to find doctors");
    } finally {
      setIsSearchingDoctors(false);
    }
  };

  const findClinicDoctors = async (selectedDate: string) => {
    try {
      const response = await AppointmentService.findAvailableDoctorslots(
        selectedDate,
        "Available"
      );

      if (response.success) {
        if (response.data.length === 0) {
          toast.error("Doctors not Available");
          setAvailableDoctors([]);
          return;
        }

        const todayDate = formattedDate();
        const currentTime = getCurrentTime();

        const filteredData = response.data.map((doctor) => {
          let availableSlots = doctor.available_slots;

          if (selectedDate === todayDate) {
            availableSlots = availableSlots.filter(
              (slot) => slot.start_time > currentTime
            );
          }
          return { ...doctor, available_slots: availableSlots };
        });

        setAvailableDoctors(
          filteredData.filter(
            (available: unknown) => !available?.doctorDetails?.isdecommissioned
          )
        );
      }
    } catch (error) {
      console.error("Error finding clinic doctors:", error);
      toast.error("Failed to find clinic doctors");
    }
  };

  const findCampDoctors = async (selectedDate: string) => {
    try {
      const [campResponse, doctorsResponse] = await Promise.all([
        MedicalCampService.getMedicalCamps({ page: 1, pageSize: 200 }),
        AppointmentService.findAvailableDoctorslots(selectedDate, "Available"),
      ]);

      if (!campResponse.success || !doctorsResponse.success) {
        toast.error("Failed to fetch data");
        return;
      }

      const userCamp = campResponse.data?.dataList.find(
        (camp) => camp.username === user?.username
      );

      if (!userCamp) {
        toast.error("No medical camp assigned to this user");
        return;
      }

      const campDoctors = userCamp.campDoctors || [];
      const todayDate = formattedDate();
      const currentTime = getCurrentTime();

      const matchedDoctors = campDoctors
        .map((campDoctor) => {
          const doctorDetails = doctorsResponse.data?.find(
            (doctor) => doctor.doctor_id === campDoctor.doctorId
          );

          if (!doctorDetails) return null;

          let availableSlots = doctorDetails.available_slots || [];

          // If the selected date is today, filter out past slots
          if (selectedDate === todayDate) {
            availableSlots = availableSlots.filter(
              (slot) => slot.start_time > currentTime
            );
          }

          return {
            ...doctorDetails,
            available_slots: availableSlots,
            amount: campDoctor.amount,
          };
        })
        .filter(Boolean);

      setAvailableDoctors(matchedDoctors);
    } catch (error) {
      console.error("Error finding camp doctors:", error);
      toast.error("Failed to find camp doctors");
    }
  };

  const handleDateFilterOnChange = (selectedDate: string) => {
    handleFindDoctors(selectedDate);
    setDateFilter(selectedDate);
  };

  const requiredFields = [
    "weight",
    "temperature",
    "pulseRate",
    "heartRate",
    "spo2",
    "glucose",
    "respiratoryRate",
    "vegetarian",
    "bloodPressureMin",
    "bloodPressureMax",
    "patientConcerns",
  ] as const;
  const validateForm = () => {
    const hasEmptyFields = requiredFields.some(
      (field) => vitalsFormData[field] === ""
    );

    return !hasEmptyFields;
  };

  const handleFormSubmit = async () => {
    console.log("trueeee");

    if (disable) {
      setButtonName("Update Vitals");
      setDisable(!disable);
    } else {
      try {
        const patientVitalsPaths = onGoingPrescriptions.map((pres) => {
          return pres.patientVitalsPath;
        });

        const patientVitalsPath = patientVitalsPaths.join(",");
        let updatedData = {
          ...vitalsFormData,
          patientVitalsPath,
          image: currentPrescriptions,
        };

        if (!isPregnant) {
          updatedData = {
            ...updatedData,
            pregnancyMonths: null,
            fetalHeartRate: null,
          };
        }

        // setVitalsFormData(updatedData);
        setIsVitalsAdded(true);
        console.log(updatedData);
        await sendFormDataToAPI(updatedData);
        setCurrentPrescriptions([]);
      } catch (error) {
        console.log("Error", error);
      } finally {
        setDisable(!disable);
        setButtonName("Edit Vitals");
      }
    }
  };

  const sendFormDataToAPI = async (data: any) => {
    try {
      toast.loading(hasVitals ? "Updating Vitals..." : "Adding Vitals...", {
        id: "loading",
      });

      const formData = new FormData();

      // Append all non-file fields dynamically
      Object.entries(data).forEach(([key, value]) => {
        if (key !== "image") {
          formData.append(key, value as string);
        }
      });

      // Append files properly
      if (data.image) {
        data.image.forEach((file: File) => {
          formData.append("image", file);
        });
      }

      // Send API request
      const response = await PatientService.addPatientVitals(formData);
      if (response.success && response.data) {
        toast.success(`Vitals ${hasVitals ? "updated" : "added"} successfully`);
        setVitalsFormData((prev) => ({
          ...prev,
          ...response.data,
        }));

        setHasVitals(true);
        setDisable(true);
        setButtonName("Edit Vitals");
        setOnGoingPrescriptions(response.data.ongoingMedication);
      } else {
        toast.error(response.message || "Failed to submit vitals");
      }
    } catch (error) {
      console.log("API Error:", error);
      toast.error(`Failed to ${hasVitals ? "Update Vitals" : "Add vitals"} `);
    } finally {
      toast.dismiss("loading");
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);
    const validationPromises = fileArray.map((file) => {
      return new Promise<File | null>((resolve) => {
        const fileType = file.type;

        // Create a sanitized file object
        const sanitizedFileName = sanitizeFileName(file.name);
        // Create a new File object with sanitized name but same content
        const sanitizedFile = new File([file], sanitizedFileName, { type: file.type });

        if (fileType.startsWith("image/")) {
          const img = new Image();
          img.onload = () => resolve(sanitizedFile); // Use sanitized file
          img.onerror = () => {
            toast.error(
              `The image file "${file.name}" is corrupt or unreadable.`
            );
            resolve(null);
          };
          img.src = URL.createObjectURL(file);
        } else if (fileType === "application/pdf") {
          const reader = new FileReader();
          reader.onload = (event) => {
            const buffer = event.target?.result as ArrayBuffer;
            const header = new TextDecoder("utf-8").decode(
              new Uint8Array(buffer.slice(0, 5))
            );
            if (header === "%PDF-") {
              resolve(sanitizedFile); // Use sanitized file
            } else {
              toast.error(`The PDF file "${file.name}" appears to be corrupt.`);
              resolve(null);
            }
          };
          reader.onerror = () => {
            toast.error(`Failed to read the PDF file "${file.name}".`);
            resolve(null);
          };
          reader.readAsArrayBuffer(file);
        } else {
          toast.error(`Unsupported file type: ${file.name}`);
          resolve(null);
        }
      });
    });

    // Once all validations complete
    Promise.all(validationPromises).then((results) => {
      const validFiles = results.filter((f): f is File => f !== null);
      if (validFiles.length > 0) {
        setCurrentPrescriptions((prev) => [...prev, ...validFiles]);
      }
    });
  };

  const handleDeleteNewPrescription = (index: number) => {
    setCurrentPrescriptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingPrescription = (index: number) => {
    console.log(index);
    setOnGoingPrescriptions((prev) => prev.filter((_, i) => i !== index));
  };
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setVitalsFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disable) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disable) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disable) {
      e.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disable) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith("image/")
      );

      if (droppedFiles.length > 0) {
        setCurrentPrescriptions((prev) => [...prev, ...droppedFiles]);
        setVitalsFormData((prev) => ({
          ...prev,
          ongoingMedication: [],
        }));
      } else {
        toast.error("Please drop only image files");
      }

      // Clear the dataTransfer object
      e.dataTransfer.clearData();
    }
  };

  // Mark as Ready appointment
  const handleMarkAsReady = async (status: boolean) => {
    try {
      const response = await AppointmentService.updateAppointmentStatus(
        appointment?.id,
        "In Consultation"
      );
      if (response.success) {
        toast.success(response.message);
        setIsmarkasready(status);

        //send notifictaion to doctor
        if (appointment.doctor_id) {
          const token = await AuthService.fetchNotificationTokens(appointment.doctor_id);
          const title = "Patient marked ready";
          const message = `Patient ${appointment.patientDetails.name} is marked as ready`

          if (typeof token === "string") {
            const res = await AuthService.sendNotification(title, message, token);
            console.log(res);
          }
          try {
            await AuthService.createNotification(title, message, appointment?.doctor_id, "Doctor");
          } catch (dbNotifyErr) {
            console.error("Error creating notification record:", dbNotifyErr);
          }
        }

        // Connection with firebase
        const docRef = doc(
          collection(db, "patientAvailability"),
          appointment?.id
        );
        await setDoc(
          docRef,
          {
            patientId: appointment?.patientDetails?.id,
            doctorId: appointment?.doctorDetails?.userId,
            clinicId: appointment?.is_medical_camp
              ? appointment?.medical_camp_id
              : appointment?.clinic_id,
            appointmentId: appointment?.id,
            age: appointment?.patientDetails?.age,
            appointmentTime: `${appointment?.appointment_start_time} - ${appointment?.appointment_end_time}`,
            appointmentDate: appointment?.appointment_date,
            callStatus: "pending",
            gender: appointment?.patientDetails?.gender,
            hospital: appointment?.doctorDetails?.hospitalName,
            image: appointment?.doctorDetails?.profileImage,
            name: appointment?.patientDetails?.name,
            mobilenumber: appointment?.patientDetails?.mobileNumber,
            doctorname: appointment?.doctorDetails?.name,
            education: appointment?.doctorDetails?.education[0],
            specialization: appointment?.doctorDetails?.specialization[0],
            patientprofile: appointment?.patientDetails?.profileImage,
            uid: null,
            roomStatus: "IDLE",
            status: "In Consultation",
            channelName: null,
          },
          { merge: true }
        );

        onClose();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(`Failed to mark as ready ${error.message}`);
    }
  };

  // Rescheduling appointment
  const handleRescheduleAppointment = async (e: React.MouseEvent) => {
    e.preventDefault();

    const appointmentData = {
      clinicId: user?.id,
      doctorId: selectedDoctor,
      patientId: appointment?.patient_id || appointment?.patientId,
      appointmentDate:
        dateFilter ||
        appointment?.appointment_date?.split("T")[0] ||
        appointment?.appointmentDate?.split("T")[0],
      startTime: selectedTimeSlot.slot.start_time,
      endTime: selectedTimeSlot.slot.end_time,
      appointmentId: appointment?.id,
    };

    try {
      const response = await AppointmentService.rescheduleAppointment(
        appointmentData
      );

      if (response.success) {
        toast.success("Appointment Rescheduled Successfully");
        // Remove the patient availability from the firebase
        await deleteDoc(doc(db, "patientAvailability", appointment?.id));
        loadAppointments();
        setisAppointmentBooked(true);
        onClose();
        setIsmarkasready(true);
      } else {
        toast.error("Failed to Rescheduled Appointment");
      }
      console.log("Selected Doctor:", selectedDoctor);
      if (selectedDoctor && response.success) {
        const tokens = await AuthService.fetchNotificationTokens(selectedDoctor);
        const title = "Appointment booked";
        const message = selectedTimeSlot
          ? `Appointment booked on ${dateFilter ?? formattedDate()} ${selectedTimeSlot.slot.start_time}-${selectedTimeSlot.slot.end_time} for ${appointment?.patientDetails?.name}`
          : "Appointment booked";
        await AuthService.sendNotification(title, message, tokens);
        try {
          await AuthService.createNotification(title, message, selectedDoctor, "Doctor");
        } catch (dbNotifyErr) {
          console.error("Error creating notification record:", dbNotifyErr);
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to Rescheduled appointment");
    } finally {
      loadAppointments();
    }
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "patientAvailability"),
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "modified") {
            console.log("Modified document: ", change.doc.id);
          }
        });
      },
      (error) => {
        console.error("Error in status sync:", error);
        toast.error("Failed to sync status updates");
      }
    );

    return () => unsubscribe();
  }, []);

  if (isLoading) {
    return (
      <div className="w-full h-[80vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
          <p className="text-gray-600 font-medium">
            Loading patient details...
          </p>
        </div>
      </div>
    );
  }

  const handlePreviewFile = (status: boolean, type: string, url: string) => {
    setSelectedFileDetails({
      status: status,
      type: type,
      url: url,
    });
  };
  // Compute readOnly based on patientAvailability and disableAccess
  const isReadOnly = disableAccess || !isPatientAvailable;

  return (
    <div className="w-full relative">
      <form
        className="h-full flex flex-col items-center justify-center"
        onSubmit={handleSubmit}
      >
        <div className="h-full flex items-stretch justify-between gap-2 mb-10">
          <div className="w-[100%]">
            {/* Weight, temperature and pulseRate*/}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="space-y-2">
                <label
                  htmlFor="weight"
                  className="text-sm text-gray-600 font-medium flex items-center gap-1"
                >
                  <Scale className="w-4 h-4" /> Weight (kg)
                </label>
                <input
                  id="weight"
                  name="weight"
                  type="number"
                  value={vitalsFormData.weight}
                  onChange={(e) => {
                    if (e.target.value.includes("-")) return;

                    handleChange(e);
                  }}
                  readOnly={isReadOnly && !isTreatedPatient}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  disabled={disable}
                  placeholder="Enter Weight in kg"
                  pattern="^\d+(\.\d+)?$"
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="temperature"
                  className="text-sm text-gray-600 font-medium flex items-center gap-1"
                >
                  <Thermometer className="w-4 h-4" /> Temperature (°C)
                </label>
                <input
                  id="temperature"
                  name="temperature"
                  type="number"
                  value={vitalsFormData.temperature}
                  onChange={handleChange}
                  readOnly={isReadOnly && !isTreatedPatient}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  disabled={disable}
                  placeholder="Enter Temperature in °C"
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="pulseRate"
                  className=" text-sm text-gray-600 font-medium flex items-center gap-1"
                >
                  <Activity className="w-4 h-4" /> Pulse Rate (bpm)
                </label>
                <input
                  id="pulseRate"
                  name="pulseRate"
                  type="number"
                  value={vitalsFormData.pulseRate}
                  onChange={handleChange}
                  readOnly={isReadOnly && !isTreatedPatient}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  disabled={disable}
                  placeholder="Enter Pluse Rate in bpm"
                  required
                />
              </div>
            </div>

            {/* heartRate, SPO2 Level and glucose*/}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="space-y-2">
                <label
                  htmlFor="heartRate"
                  className="text-sm text-gray-600 font-medium flex items-center gap-1"
                >
                  <Heart className="w-4 h-4" /> Heart Rate (bpm)
                </label>
                <input
                  id="heartRate"
                  name="heartRate"
                  type="number"
                  value={vitalsFormData.heartRate}
                  onChange={handleChange}
                  readOnly={isReadOnly && !isTreatedPatient}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  disabled={disable}
                  placeholder="Enter Heart Rate in bpm"
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="spo2"
                  className="text-sm text-gray-600 font-medium flex items-center gap-1"
                >
                  <Droplet className="w-4 h-4" /> SPO2 Level (%)
                </label>
                <input
                  id="spo2"
                  name="spo2"
                  type="number"
                  value={vitalsFormData.spo2}
                  onChange={handleChange}
                  readOnly={isReadOnly && !isTreatedPatient}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  disabled={disable}
                  placeholder="Enter SPO2 Level in %"
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="glucose"
                  className="text-sm text-gray-600 font-medium flex items-center gap-1"
                >
                  <CandyCane className="w-4 h-4" /> Glucose Level (mg/dL)
                </label>
                <input
                  id="glucose"
                  name="glucose"
                  type="number"
                  value={vitalsFormData.glucose}
                  onChange={handleChange}
                  readOnly={isReadOnly && !isTreatedPatient}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  disabled={disable}
                  placeholder="Enter Glucose Level in mg/dL"
                  required
                />
              </div>
            </div>

            {/* respiratoryRate, Vegetarian / Non-Vegetarian and bloodPressure*/}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="space-y-2">
                <label
                  htmlFor="respiratoryRate"
                  className="text-sm text-gray-600 font-medium flex items-center gap-1"
                >
                  <Wind className="w-4 h-4" /> Respiratory Rate (breaths/min)
                </label>
                <input
                  id="respiratoryRate"
                  name="respiratoryRate"
                  type="number"
                  value={vitalsFormData.respiratoryRate}
                  onChange={handleChange}
                  readOnly={isReadOnly && !isTreatedPatient}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  disabled={disable}
                  placeholder="Enter Respiratory Rate"
                  required
                />
              </div>

              <div className="vegetarian">
                <div className="space-y-2">
                  <label
                    htmlFor="vegetarian"
                    className="text-sm text-gray-600 font-medium flex items-center gap-1"
                  >
                    <Salad className="w-4 h-4" /> Vegetarian / Non-Vegetarian
                  </label>
                  <select
                    id="vegetarian"
                    name="vegetarian"
                    className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                    value={vitalsFormData.vegetarian}
                    onChange={handleChange}
                    disabled={disable || isReadOnly}
                    required
                  >
                    <option value="" disabled>
                      Select an option
                    </option>
                    <option value="true">Vegetarian</option>
                    <option value="false">Non-Vegetarian</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="bloodPressureMin"
                  className="text-sm text-gray-600 font-medium flex items-center gap-1"
                >
                  <Gauge className="w-4 h-4" /> Blood Pressure (mmHg)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    id="bloodPressureMin"
                    name="bloodPressureMin"
                    type="number"
                    value={vitalsFormData.bloodPressureMin}
                    onChange={(e) => {
                      const minValue = parseInt(e.target.value, 10);
                      const maxValue = parseInt(
                        vitalsFormData.bloodPressureMax,
                        10
                      );
                      if (
                        !isNaN(minValue) &&
                        !isNaN(maxValue) &&
                        minValue >= maxValue
                      ) {
                        toast.error(
                          "Diastolic pressure must be less than systolic pressure!"
                        );
                        return;
                      }

                      handleChange(e);
                    }}
                    readOnly={isReadOnly && !isTreatedPatient}
                    className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                    disabled={disable}
                    placeholder="Systolic"
                    required
                  />
                  <input
                    id="bloodPressureMax"
                    name="bloodPressureMax"
                    type="number"
                    value={vitalsFormData.bloodPressureMax}
                    onChange={handleChange}
                    readOnly={isReadOnly && !isTreatedPatient}
                    className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                    disabled={disable}
                    placeholder="Diastolic"
                    required
                  />
                </div>
              </div>
            </div>

            {(appointment?.patientDetails?.gender === "Female" ||
              appointment?.gender === "Female") && (
                /* heartRate, SPO2 Level and glucose*/
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="Pregnancy">
                    <div className="space-y-2">
                      <label
                        htmlFor="Pregnancy"
                        className="text-sm text-gray-600 font-medium flex items-center gap-1"
                      >
                        <Baby className="w-4 h-4" /> Is Pregnant ?
                      </label>
                      <select
                        id="pregnancy"
                        name="pregnancy"
                        className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                        value={isPregnant.toString()}
                        onChange={(e) => setIsPregnant(e.target.value === "true")}
                        disabled={disable || isReadOnly}
                        required
                      >
                        <option value="" disabled>
                          Select an option
                        </option>
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </div>
                  </div>
                  {isPregnant && (
                    <>
                      <div className="space-y-2">
                        <label
                          htmlFor="spo2"
                          className="text-sm text-gray-600 font-medium flex items-center gap-1"
                        >
                          <CalendarClock className="w-4 h-4" /> Pregnancy months
                        </label>
                        <input
                          id="pregnancyMonths"
                          name="pregnancyMonths"
                          type="number"
                          value={vitalsFormData.pregnancyMonths}
                          onChange={handleChange}
                          readOnly={isReadOnly && !isTreatedPatient}
                          className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                          disabled={disable}
                          placeholder="Enter pregnancy months"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <label
                          htmlFor="glucose"
                          className="text-sm text-gray-600 font-medium flex items-center gap-1"
                        >
                          <HeartPulse className="w-4 h-4" /> Fetal Heart Rate
                          (bpm)
                        </label>
                        <input
                          id="fetalHeartRate"
                          name="fetalHeartRate"
                          type="number"
                          value={vitalsFormData.fetalHeartRate}
                          onChange={handleChange}
                          readOnly={isReadOnly && !isTreatedPatient}
                          className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                          disabled={disable}
                          placeholder="Enter Fetal's Heart Rate in bpm"
                          required
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

            {/* Ongoing Prescriptions / Active Medications */}
            {onGoingPrescriptions.length > 0 ||
              currentPrescriptions.length > 0 ? (
              <div className="w-full flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-[#3F5260] text-sm font-medium flex items-center gap-1">
                    <Stethoscope className="w-4 h-4" /> Ongoing Prescriptions /
                    Active Medications
                  </h3>
                  {!isTreatedPatient && (
                    <label
                      htmlFor="prescription-image"
                      className="cursor-pointer text-sm outline-none font-md text-[#647588]"
                    >
                      <input
                        type="file"
                        id="prescription-image"
                        name="prescriptionImage"
                        accept="image/*, application/pdf"
                        multiple
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={disable || isReadOnly}
                      />
                      <SquarePen className="w-4 h-4" />
                    </label>
                  )}
                </div>

                {/* Images and Upload Button */}
                <div className="flex flex-wrap items-center gap-4 my-4">
                  {onGoingPrescriptions.map((vitalsImage, index) => (
                    <>
                      {vitalsImage.patientVitalsImage.includes(".pdf") && (
                        <div
                          key={`ongoing-${index}`}
                          className="relative cursor-pointer rounded-tr-2xl rounded-md bg-white border border-black/30 shadow-md w-[80px] h-[100px] flex items-start justify-end p-2"
                          onClick={() =>
                            window.open(
                              vitalsImage.patientVitalsImage,
                              "_blank"
                            )
                          }
                        >
                          <span className="text-red-500 text-xs font-bold">
                            PDF
                          </span>
                          <span className="mt-6 absolute top-4 left-2 border border-gray-400/80 w-[80%] "></span>
                          <span className="mt-8 absolute top-4 left-2 border border-gray-400/80 w-[80%] "></span>
                          <span className="mt-10 absolute top-4 left-2 border border-gray-400/80 w-[80%] "></span>
                          <span className="mt-12 absolute top-4 left-2 border border-gray-400/80 w-[80%] "></span>
                          <span className="mt-14 absolute top-4 left-2 border border-gray-400/80 w-[80%] "></span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteExistingPrescription(index);
                            }}
                            type="button"
                            className="bg-white absolute bottom-2 right-2 w-3 h-3 outline-none font-md text-[#FF6C6C]"
                            disabled={disable || isReadOnly}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      <>
                        {vitalsImage?.patientVitalsImage?.includes(".img") && (
                          <div
                            key={`ongoing-${index}`}
                            className="relative rounded-tr-2xl rounded-md bg-white border border-black/30 shadow-md w-[80px] h-[100px] flex flex-col items-center justify-center p-2"
                          >
                            <img
                              src={vitalsImage.patientVitalsImage}
                              alt={`Ongoing Prescription ${index + 1}`}
                              className="w-14 h-14 rounded-md cursor-pointer m-1"
                              onClick={() =>
                                handlePreviewFile(
                                  true,
                                  "image",
                                  vitalsImage.patientVitalsImage
                                )
                              }
                            />
                            <button
                              onClick={() =>
                                handleDeleteExistingPrescription(index)
                              }
                              type="button"
                              className="absolute bottom-1 right-1 text-sm outline-none font-md text-[#FF6C6C]"
                              disabled={disable || isReadOnly}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </>
                    </>
                  ))}

                  {/* Newly Uploaded Prescriptions */}
                  {currentPrescriptions.map((file, index) => (
                    <div key={`new-${index}`} className="relative">
                      {file.type.includes("pdf") ? (
                        <div
                          className="relative cursor-pointer rounded-tr-2xl rounded-md bg-white border border-black/30 shadow-md w-[80px] h-[100px] flex items-start justify-end p-2"
                          onClick={() =>
                            window.open(URL.createObjectURL(file), "_blank")
                          }
                        >
                          <span className="text-red-500 text-xs font-bold">
                            PDF
                          </span>
                          <span className="mt-6 absolute top-4 left-2 border border-gray-400/80 w-[80%]"></span>
                          <span className="mt-8 absolute top-4 left-2 border border-gray-400/80 w-[80%]"></span>
                          <span className="mt-10 absolute top-4 left-2 border border-gray-400/80 w-[80%]"></span>
                          <span className="mt-12 absolute top-4 left-2 border border-gray-400/80 w-[80%]"></span>
                          <span className="mt-14 absolute top-4 left-2 border border-gray-400/80 w-[80%]"></span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNewPrescription(index);
                            }}
                            type="button"
                            className="bg-white absolute bottom-2 right-2 w-3 h-3 outline-none font-md text-[#FF6C6C]"
                            disabled={disable || isReadOnly}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div
                            key={`current-${index}`}
                            className="relative rounded-tr-2xl rounded-md bg-white border border-black/30 shadow-md w-[80px] h-[100px] flex flex-col items-center justify-center p-2"
                          >
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`New Prescription ${index + 1}`}
                              className="w-14 h-14 rounded-md cursor-pointer m-1"
                              onClick={() =>
                                handlePreviewFile(
                                  true,
                                  "image",
                                  URL.createObjectURL(file)
                                )
                              }
                            />
                            <button
                              onClick={() => handleDeleteNewPrescription(index)}
                              type="button"
                              className="absolute bottom-1 right-1 text-sm outline-none font-md text-[#FF6C6C]"
                              disabled={disable || isReadOnly}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {currentPrescriptions.length < 6 ? (
                    <label
                      htmlFor="prescription-image"
                      className="cursor-pointer text-sm outline-none font-md text-[#647588]"
                    >
                      <input
                        type="file"
                        id="prescription-image"
                        name="prescriptionImage"
                        accept="image/*, application/pdf"
                        multiple
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                      <PlusCircle className="w-6 h-6" />
                    </label>
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                {user?.role !== "admin" && !isTreatedPatient && (
                  <label
                    ref={dropZoneRef}
                    htmlFor="prescription-image"
                    className={`w-full cursor-pointer rounded-md flex items-center flex-col justify-center bg-gray-100 py-2 ${isDragging
                      ? "border-2 border-dashed border-emerald-500 bg-emerald-50"
                      : ""
                      } ${isReadOnly ? "hidden cursor-not-allowed" : ""}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      id="prescription-image"
                      name="prescriptionImage"
                      accept="image/*, application/pdf"
                      multiple
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={disable || isReadOnly}
                    />
                    <label
                      htmlFor="prescription-image"
                      className={`cursor-pointer w-11 h-11 mx-auto mb-2 rounded-full bg-emerald-600 flex items-center justify-center ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                    >
                      <Upload className="w-5 h-5 text-white" />
                    </label>
                    <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                      {isDragging ? (
                        <>
                          <Download className="w-4 h-4" /> Drop images here
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" /> Click to upload or drag
                          and drop your Prescriptions
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                      <FileText className="w-3 h-3" /> Upload prescription
                      (optional)
                    </p>
                  </label>
                )}
              </>
            )}

            <span className="text-[#771D51] font-medium text-sm flex items-center gap-1 mt-2">
              <Calendar className="w-4 h-4" />
              {patientAppointmentHistory &&
                Object.keys(patientAppointmentHistory).length > 0 &&
                patientAppointmentHistory.date
                ? `Vitals Added Date: ${patientAppointmentHistory.date}`
                : `Current Vitals Date: ${formattedDate(currentDate)}`}
            </span>
          </div>
        </div>

        {/* Buttons Container */}
        <div className="p-4 border-t border-[#E2E2F1] w-full flex items-center flex-row-reverse justify-start gap-4">
          {user?.role !== "admin" &&
            isPatientAvailable &&
            !isTreatedPatient && (
              <button
                type="button"
                className={`disabled:cursor-not-allowed font-bold transition-colors text-sm text-white flex items-center justify-center p-2 px-4 rounded-md bg-[#199C4D] hover:bg-[#116b35] disabled:opacity-40`}
                onClick={handleFormSubmit}
              >
                <Save className="w-4 h-4 mr-1" /> {buttonName}
              </button>
            )}

          {(appointment?.patient_id || appointment?.patientId) &&
            user?.role !== "admin" && (
              <>
                {destination && (
                  <>
                    {destination !== "upcoming-appointments" && (
                      <button
                        onClick={() => handleMarkAsReady(true)}
                        type="button"
                        className="disabled:cursor-not-allowed font-bold disabled:opacity-40 transition-colors text-sm text-white flex items-center justify-center p-2 px-4 rounded-md bg-[#199C4D] hover:bg-[#116b35]"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Mark Ready
                      </button>
                    )}
                  </>
                )}
              </>
            )}
        </div>
      </form>

      {isRescheduling && (
        <div className="flex items-center justify-start flex-col hiddenScrollbar overflow-hidden overflow-y-auto absolute bottom-[5.25rem] right-0 border-2 border-green-700/70 bg-white h-[600px] w-[550px] p-2 rounded-md">
          <div className="w-full p-2 flex items-center justify-end">
            <button
              className="transition-colors text-sm hover:text-red-600"
              onClick={() => setIsrescheduling(false)}
            >
              <X className="w-7 h-7 " />
            </button>
          </div>
          <FindDoctorBox
            isSearchingDoctors={isSearchingDoctors}
            availableDoctors={availableDoctors}
            selectedDoctor={selectedDoctor}
            isAppointmentBooked={isAppointmentBooked}
            selectedTimeSlot={selectedTimeSlot}
            setSelectedDoctor={setSelectedDoctor}
            setSelectedTimeSlot={setSelectedTimeSlot}
            handleBookAppointment={handleRescheduleAppointment}
            handleDateFilterOnChange={handleDateFilterOnChange}
            dateFilter={dateFilter}
            handleFindDoctors={handleFindDoctors}
            isVerified={true}
          />
        </div>
      )}

      {selectedFileDetatils.status && (
        <FilePreview
          onClose={() => handlePreviewFile(false, "", "")}
          fileDetails={selectedFileDetatils}
        />
      )}
    </div>
  );
}
